"use client"

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/auth-context'
import { motion } from 'framer-motion'
import { Mail } from 'lucide-react'
import { auth } from '../../lib/firebase'

export default function Login({ isSignup = false, showVerificationPrompt: initialShowVerificationPrompt = false }) {
  const navigate = useNavigate()
  const { login, signup, resetPassword, sendVerificationEmail } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(initialShowVerificationPrompt)
  const [showResetPassword, setShowResetPassword] = useState(false)

  // Remove the redundant auth listener effect and keep just the verification check
  useEffect(() => {
    if (showVerificationPrompt) {
      const checkVerification = async () => {
        try {
          await auth.currentUser?.reload()
          if (auth.currentUser?.emailVerified) {
            setShowVerificationPrompt(false)
            navigate('/chat')
          }
        } catch (err) {
          console.error('Error checking verification status:', err)
        }
      }

      const interval = setInterval(checkVerification, 3000) // Check every 3 seconds
      return () => clearInterval(interval)
    }
  }, [showVerificationPrompt, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (isSignup) {
        await signup(email, password)
        setShowVerificationPrompt(true)
      } else {
        await login(email, password)
        // Only navigate if login is successful (which means email is verified)
        navigate('/chat')
      }
    } catch (err) {
      if (err.message.includes('verify your email')) {
        setShowVerificationPrompt(true)
      } else {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }

  // Keep email up to date with current user's email if not set
  useEffect(() => {
    if (!email && auth.currentUser?.email) {
      setEmail(auth.currentUser.email)
    }
  }, [email])

  const handleResendVerification = async () => {
    try {
      await sendVerificationEmail()
      setSuccess('Verification email sent! Please check your inbox.')
    } catch (err) {
      setError('Failed to send verification email. Please try again.')
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    if (!email) {
      setError('Please enter your email address')
      return
    }
    
    setLoading(true)
    try {
      await resetPassword(email)
      setSuccess('Password reset email sent! Please check your inbox.')
      setShowResetPassword(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (showVerificationPrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-8"
        >
          <div className="text-center">
            <Mail className="w-16 h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-2xl font-bold text-white mb-4">Verify Your Email</h2>
            <p className="text-gray-400 mb-6">
              We've sent a verification email to <strong>{email}</strong>. Please check your inbox and click the verification link.
            </p>
            {success && (
              <div className="mb-4 p-3 rounded bg-green-500/10 border border-green-500/50 text-green-500">
                {success}
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/50 text-red-500">
                {error}
              </div>
            )}
            <button
              onClick={handleResendVerification}
              className="w-full py-3 px-4 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors mb-4"
            >
              Resend Verification Email
            </button>
            <button
              onClick={() => navigate('/login')}
              className="text-purple-400 hover:text-purple-300"
            >
              Back to Login
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  if (showResetPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6">Reset Password</h2>
          {error && (
            <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/50 text-red-500">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded bg-green-500/10 border border-green-500/50 text-green-500">
              {success}
            </div>
          )}
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-1" htmlFor="reset-email">
                Email
              </label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="you@example.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button
              type="button"
              onClick={() => setShowResetPassword(false)}
              className="w-full text-purple-400 hover:text-purple-300"
            >
              Back to Login
            </button>
          </form>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex flex-col">
      <header className="fixed top-0 w-full bg-black/50 backdrop-blur-md border-b border-gray-800">
        <nav className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/')}
            className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text"
          >
            Verdyct
          </button>
        </nav>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-8">
            <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text">
              {isSignup ? 'Create Account' : 'Welcome Back'}
            </h2>
            
            {error && (
              <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/50 text-red-500">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-1" htmlFor="login-email">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-1" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="••••••••"
                  required
                />
              </div>

              {!isSignup && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(true)}
                    className="text-sm text-purple-400 hover:text-purple-300"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2" />
                    {isSignup ? 'Creating Account...' : 'Logging In...'}
                  </div>
                ) : (
                  isSignup ? 'Create Account' : 'Log In'
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-gray-400">
              {isSignup ? (
                <>
                  Already have an account?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-purple-400 hover:text-purple-300 font-semibold"
                  >
                    Log In
                  </button>
                </>
              ) : (
                <>
                  Don't have an account?{' '}
                  <button
                    onClick={() => navigate('/signup')}
                    className="text-purple-400 hover:text-purple-300 font-semibold"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

